import { StateCreator } from 'zustand';

import { AppStore } from '../appStore';

// Flow step enum defining the sequence
export enum FlowStep {
  LANDING = 'landing',
  SCENE_DYNAMIC = 'scene-dynamic',
  SCENE_PLANNING = 'scene-planning',
  EDITOR = 'editor',
}

// Flow state interface
export interface FlowState {
  currentStep: FlowStep;
  completedSteps: Set<FlowStep>;
  maxAllowedStep: FlowStep;
}

// Serializable flow state for persistence
export interface SerializableFlowState {
  currentStep: FlowStep;
  completedSteps: FlowStep[];
  maxAllowedStep: FlowStep;
}

// Flow actions interface
export interface FlowActions {
  goToStep: (step: FlowStep) => boolean;
  markStepCompleted: (step: FlowStep) => void;
  canNavigateToStep: (step: FlowStep) => boolean;
  validateAndProgressToNextStep: () => boolean;
  goToPreviousStep: () => void;
  resetFlow: () => void;
  validateAndRestoreFlow: () => void;
}

// Combined flow slice interface
export interface FlowSlice {
  flow: FlowState;
  flowActions: FlowActions;
}

// Step progression order
const STEP_ORDER: FlowStep[] = [
  FlowStep.LANDING,
  FlowStep.SCENE_DYNAMIC,
  FlowStep.SCENE_PLANNING,
  FlowStep.EDITOR,
];

// Get the index of a step in the progression
const getStepIndex = (step: FlowStep): number => {
  return STEP_ORDER.indexOf(step);
};

// Initial flow state
export const createInitialFlowState = (): FlowState => ({
  currentStep: FlowStep.LANDING,
  completedSteps: new Set<FlowStep>(),
  maxAllowedStep: FlowStep.LANDING,
});

export const initialFlowState: FlowState = createInitialFlowState();

// Convert flow state to serializable format
export const serializeFlowState = (state: FlowState): SerializableFlowState => ({
  currentStep: state.currentStep,
  completedSteps: Array.from(state.completedSteps),
  maxAllowedStep: state.maxAllowedStep,
});

// Convert serializable format back to flow state
export const deserializeFlowState = (serialized: SerializableFlowState): FlowState => {
  // Handle case where completedSteps might not be an array
  let completedSteps: Set<FlowStep>;

  if (Array.isArray(serialized.completedSteps)) {
    completedSteps = new Set(serialized.completedSteps);
  } else if (serialized.completedSteps && typeof serialized.completedSteps === 'object') {
    // Handle case where it might be serialized as an object (legacy format)
    // Try to extract values from the object
    try {
      const values = Object.values(serialized.completedSteps as any).filter(
        (value): value is FlowStep =>
          typeof value === 'string' && Object.values(FlowStep).includes(value as FlowStep)
      );
      completedSteps = new Set(values);
    } catch {
      completedSteps = new Set();
    }
  } else {
    completedSteps = new Set();
  }

  return {
    currentStep: serialized.currentStep || FlowStep.LANDING,
    completedSteps,
    maxAllowedStep: serialized.maxAllowedStep || FlowStep.LANDING,
  };
};

// Helper function to validate step completion
function validateStepCompletion(step: FlowStep, state: AppStore): boolean {
  switch (step) {
    case FlowStep.LANDING: {
      // Landing is always considered complete when leaving it
      return true;
    }

    case FlowStep.SCENE_DYNAMIC: {
      // Requires dynamic type selection and 50+ words in both text fields
      const { dynamicType, realConversation, fictionalAnalysis } = state.planningState;
      const realWords = realConversation
        .trim()
        .split(/\s+/)
        .filter((w: string) => w.length > 0).length;
      const fictionalWords = fictionalAnalysis
        .trim()
        .split(/\s+/)
        .filter((w: string) => w.length > 0).length;

      return dynamicType !== '' && realWords >= 50 && fictionalWords >= 50;
    }

    case FlowStep.SCENE_PLANNING: {
      // Requires any content in all fields
      const {
        playerRole,
        npcRole,
        playerName,
        npcName,
        playerMotivation,
        npcMotivation,
        aPlot,
        stakes,
        locationAtmosphere,
      } = state.planningState;

      return (
        playerRole.trim().length > 0 &&
        npcRole.trim().length > 0 &&
        playerName.trim().length > 0 &&
        npcName.trim().length > 0 &&
        playerMotivation.trim().length > 0 &&
        npcMotivation.trim().length > 0 &&
        aPlot.trim().length > 0 &&
        stakes.trim().length > 0 &&
        locationAtmosphere.trim().length > 0
      );
    }

    case FlowStep.EDITOR: {
      // Editor completion is not relevant for flow progression
      return true;
    }

    default:
      return false;
  }
}

// Create the flow slice
export const createFlowSlice: StateCreator<AppStore, [], [], FlowSlice> = (set, get) => ({
  flow: createInitialFlowState(),

  flowActions: {
    // Navigate to a specific step if allowed
    goToStep: (step: FlowStep): boolean => {
      const { flowActions } = get();

      if (!flowActions.canNavigateToStep(step)) {
        return false;
      }

      set((state: AppStore) => ({
        flow: {
          ...state.flow,
          currentStep: step,
        },
        hasUnsavedChanges: true,
      }));

      // Update hash fragment for visual feedback
      window.location.hash = step === FlowStep.LANDING ? '' : step;

      // Save current project to persist flow position
      setTimeout(() => {
        const state = get();
        if (state.currentProjectId) {
          state.saveCurrentProject();
        }
      }, 0);

      return true;
    },

    // Mark a step as completed and update max allowed step
    markStepCompleted: (step: FlowStep) => {
      set((state: AppStore) => {
        const newCompletedSteps = new Set(state.flow.completedSteps);
        newCompletedSteps.add(step);

        // Update max allowed step
        const stepIndex = getStepIndex(step);
        const nextStepIndex = stepIndex + 1;
        let newMaxAllowedStep = state.flow.maxAllowedStep;

        if (nextStepIndex < STEP_ORDER.length) {
          const nextStep = STEP_ORDER[nextStepIndex];
          const currentMaxIndex = getStepIndex(state.flow.maxAllowedStep);

          if (nextStepIndex > currentMaxIndex) {
            newMaxAllowedStep = nextStep;
          }
        }

        return {
          flow: {
            ...state.flow,
            completedSteps: newCompletedSteps,
            maxAllowedStep: newMaxAllowedStep,
          },
          hasUnsavedChanges: true,
        };
      });

      // Save current project to persist flow completion
      setTimeout(() => {
        const state = get();
        if (state.currentProjectId) {
          state.saveCurrentProject();
        }
      }, 0);
    },

    // Check if navigation to a step is allowed
    canNavigateToStep: (step: FlowStep): boolean => {
      const { flow } = get();
      const stepIndex = getStepIndex(step);
      const maxAllowedIndex = getStepIndex(flow.maxAllowedStep);

      // Can navigate to any step up to and including max allowed
      return stepIndex <= maxAllowedIndex;
    },

    // Validate current step and progress to next
    validateAndProgressToNextStep: (): boolean => {
      const state = get();
      const { flow, flowActions } = state;
      const currentIndex = getStepIndex(flow.currentStep);

      // Validate current step completion
      if (!validateStepCompletion(flow.currentStep, state)) {
        return false;
      }

      // Mark current step as completed
      flowActions.markStepCompleted(flow.currentStep);

      // Move to next step
      if (currentIndex < STEP_ORDER.length - 1) {
        const nextStep = STEP_ORDER[currentIndex + 1];
        return flowActions.goToStep(nextStep);
      }

      return false;
    },

    // Go to previous step
    goToPreviousStep: () => {
      const { flow, flowActions } = get();
      const currentIndex = getStepIndex(flow.currentStep);

      if (currentIndex > 0) {
        const previousStep = STEP_ORDER[currentIndex - 1];
        flowActions.goToStep(previousStep);
      }
    },

    // Reset flow to initial state
    resetFlow: () => {
      set({
        flow: createInitialFlowState(),
        hasUnsavedChanges: true,
      });
      window.location.hash = '';
    },

    // Validate and restore flow state on app load
    validateAndRestoreFlow: () => {
      const { flow, flowActions } = get();

      // Check hash fragment
      const hash = window.location.hash.slice(1);
      if (hash) {
        // Validate hash is a valid step
        const hashStep = hash as FlowStep;
        if (STEP_ORDER.includes(hashStep) && flowActions.canNavigateToStep(hashStep)) {
          flowActions.goToStep(hashStep);
          return;
        }
      }

      // If no valid hash or hash not allowed, stay at current step
      // Update hash to match current step
      window.location.hash = flow.currentStep === FlowStep.LANDING ? '' : flow.currentStep;
    },
  },
});
